import React from "react";
import { Model } from "../../model_gen/model";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { cn } from "../../lib/utils";
import moment from "moment";

interface ModelsTableProps {
  models: Model[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  emptyTitle?: string;
  emptyDescription?: string;
}

const ModelsTable: React.FC<ModelsTableProps> = ({
  models,
  loading,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  emptyTitle = "No fine-tuned models found",
  emptyDescription = "No fine-tuned models have been created yet."
}) => {
  const formatDate = (timestamp: number) => {
    if (timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    if (timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const columns: PaginatedTableColumn<Model>[] = [
    {
      key: "model",
      header: "Model Name",
      width: "w-[35%]",
      render: (model) => (
        <div className="text-sm font-gc-reg text-foreground">
          {model.model}
        </div>
      )
    },
    {
      key: "base_model", 
      header: "Base Model",
      width: "w-[25%]",
      render: (model) => (
        <span className="text-sm font-gc-reg text-foreground">
          {model.base_model || "-"}
        </span>
      )
    },
    {
      key: "status",
      header: "Status", 
      width: "w-[15%]",
      render: (model) => (
        model.status ? (
          <div className={cn(
            "inline-flex items-center px-2 py-1 rounded text-xs font-medium",
            model.status.toLowerCase() === "active" || model.status.toLowerCase() === "enabled" 
              ? "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
              : model.status.toLowerCase() === "disabled"
              ? "bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400"
              : model.status.toLowerCase() === "pending"
              ? "bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400"
              : "bg-muted text-foreground"
          )}>
            {model.status.toUpperCase()}
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )
      )
    },
    {
      key: "date_added",
      header: "Date Created",
      width: "w-[25%]",
      render: (model) => (
        model.date_added ? (
          <div className="space-y-1">
            <div className="text-sm font-gc-reg text-foreground">
              {formatDate(model.date_added)}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatTime(model.date_added)}
            </div>
          </div>
        ) : (
          <span className="text-sm text-muted-foreground">-</span>
        )
      )
    }
  ];

  return (
    <PaginatedTable
      data={models}
      columns={columns}
      loading={loading}
      currentPage={currentPage}
      onPageChange={onPageChange}
      pageSize={itemsPerPage}
      totalItems={totalItems}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      serverSidePagination={true}
    />
  );
};

export default ModelsTable; 